// "I changed my mind — this drum is not finished after all."
//
// Adding a phase behind a drum the warehouse is about to take (or has taken)
// puts the whole thing in reverse: the job reopens in Projects, the drum leaves
// the Production → Warehouse queue and reappears in Project Bobin State, and any
// StorageItem the warehouse had already made for it is released.
//
// All of that is one endpoint. This is the dialog in front of it, shared by the
// two places a finished drum is looked at — the Production → Warehouse table and
// the yard in Factory 3D — because "the same thing as the table does" is the
// requirement, and two copies of it would not stay the same for long.
import { useState } from 'react';
import axios from 'axios';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { API } from '../../config/api';

// Adding a phase is a PRODUCTION edit, so it goes to the jobs service — the
// warehouse only gets told to let go of the drum, by that service.
const prod = axios.create({ baseURL: API.REDIS, timeout: 15000 });
prod.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['x-auth-token'] = token;
  return config;
});

// Phases a finished drum can still be sent to. Coiling and rewinding are the
// everyday ones; an outer sheath added this late is rare but real (a lot sold
// bare that a customer then wants jacketed).
export const ADDABLE_PHASES = [
  { value: 'coiling', label: 'Coiling', hint: 'Cut the drum into coils' },
  { value: 'rewinding', label: 'Rewinding', hint: 'Drum → drum transfer' },
  {
    value: 'exterior_sheat',
    label: 'Exterior sheath',
    hint: 'Rare — jacket a cable that was finished bare'
  }
];

/**
 * `target` is whatever the caller knows about the drum. Only `jobId` is
 * required; `tokenId` narrows the change to this drum instead of every drum at
 * the same phase, and `phase` says which phase the new one follows.
 *
 * `onDone({ ok, variant, message, data })` reports back so the caller can flash
 * it in its own style and refetch its own lists.
 */
export default function AddPhaseDialog({ target, onHide, onDone }) {
  const [phaseName, setPhaseName] = useState('coiling');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const show = !!target;

  const submit = async () => {
    if (!target?.jobId) {
      setError('This drum has no job attached — cannot add a phase to it.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const { data } = await prod.post(
        `/api/redis/jobs/${target.jobId}/add-phase`,
        {
          name: phaseName,
          ...(target.tokenId ? { tokenIds: [target.tokenId] } : {}),
          ...(target.phase ? { dependsOn: target.phase } : {})
        }
      );

      const label =
        ADDABLE_PHASES.find(p => p.value === phaseName)?.label || phaseName;
      // The phase is added either way; a drum the warehouse refused to release
      // (already on a rack) is the one thing left for a human to do, so it is
      // reported as a warning rather than swallowed as success.
      const failed = data?.releaseErrors || [];

      onDone?.({
        ok: true,
        variant: failed.length ? 'warning' : 'success',
        message: failed.length
          ? `${label} added, but the warehouse kept the drum: ${failed
              .map(f => f.error)
              .join('; ')}`
          : `${label} added — ${target.cableName || 'the drum'} is back in ` +
            'production. Plan it from Project Token State.',
        data
      });
      onHide?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    setError('');
    onHide?.();
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">
          Add phase — {target?.cableName || 'drum'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-body-tertiary fs-9">
          {target?.displayId}
          {target?.lengthMeters
            ? ` · ${Number(target.lengthMeters).toLocaleString()} m`
            : ''}
          {target?.phase
            ? ` · finished at ${String(target.phase).replaceAll('_', ' ')}`
            : ''}
        </p>

        <Form.Group className="mb-3">
          <Form.Label>Phase to add</Form.Label>
          <Form.Select
            value={phaseName}
            autoFocus
            disabled={busy}
            onChange={e => setPhaseName(e.target.value)}
          >
            {ADDABLE_PHASES.map(p => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-body-tertiary">
            {ADDABLE_PHASES.find(p => p.value === phaseName)?.hint}
          </Form.Text>
        </Form.Group>

        {error && (
          <Alert variant="danger" className="py-2 fs-9">
            {error}
          </Alert>
        )}

        <Alert variant="info" className="py-2 mb-0 fs-9">
          The drum goes back to production: the job reopens in{' '}
          <strong>Projects</strong>, the drum leaves the warehouse queue and
          reappears in <strong>Project Token State</strong>. Nothing is
          scheduled — pick it there and run the next-phase wizard to choose a
          machine and a length.
          {target?.locationKind === 'slot' && (
            <span className="d-block text-warning mt-1">
              This drum is already on a rack — take it off in the warehouse
              first, or the release will be refused.
            </span>
          )}
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} disabled={busy}>
          {busy ? 'Adding…' : 'Add phase'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
